'use client'

import React, { useMemo } from 'react'
import { ProtectionLatencyCard } from './ProtectionLatencyCard'
import { parseDbTimestamp } from '@/lib/dates'

// One clock of CTT Pilot Overview's time to takedown: from detection (the
// entry's first pipeline step) or from the domain's registration (WHOIS
// creation date) to the domain dead (backend getSourceTakedownTimes: the
// first dead probe after the last alive one). Rendered as the SIBS latency
// widget with day buckets, median and mean on top. A dead entry is timed
// only when both ends are known: a domain never seen alive, or only marked
// inactive by hand, has no death time, and a missing creation date leaves
// the registration clock untimed.

export type TakedownEntry = {
  created_at: string
  detected_at: string | null
  registered_at: string | null
  has_domain: boolean
  dead: boolean
  died_at: string | null
}

export type TakedownClock = 'detected_at' | 'registered_at'

const CLOCK_LABEL: Record<TakedownClock, string> = {
  detected_at: 'Time from detection to domain dead',
  registered_at: 'Time from registration to domain dead',
}

const DAY = 24 * 60
const BUCKETS: Array<{ label: string; maxMinutes: number; color: string }> = [
  { label: '<1 d', maxMinutes: DAY, color: 'var(--success)' },
  { label: '1-3 d', maxMinutes: 3 * DAY, color: 'var(--success)' },
  { label: '3-7 d', maxMinutes: 7 * DAY, color: 'var(--warning)' },
  { label: '7-30 d', maxMinutes: 30 * DAY, color: '#f97316' },
  { label: '>30 d', maxMinutes: Infinity, color: 'var(--destructive)' },
]
const bucketColor = (key: string) => BUCKETS.find((b) => b.label === key)?.color ?? 'var(--subtle-text)'

function median(values: number[]): number | null {
  if (values.length === 0) return null
  const sorted = [...values].sort((a, b) => a - b)
  const mid = Math.floor(sorted.length / 2)
  return sorted.length % 2 ? sorted[mid] : (sorted[mid - 1] + sorted[mid]) / 2
}

function formatDuration(minutes: number | null): string {
  if (minutes == null || !Number.isFinite(minutes)) return '—'
  const m = Math.max(0, minutes)
  if (m < 60) return `${Math.round(m)} min`
  if (m < 2 * DAY) return `${Math.round(m / 60)} h`
  return `${(m / DAY).toFixed(1)} d`
}

export function TakedownClockCard({ entries, clock, isDark }: { entries: TakedownEntry[]; clock: TakedownClock; isDark: boolean }) {
  const stats = useMemo(() => {
    const minutes: number[] = []
    let dead = 0
    for (const e of entries) {
      if (!e.dead) continue
      dead++
      const start = parseDbTimestamp(e[clock])
      const died = parseDbTimestamp(e.died_at)
      if (start && died) minutes.push((died.getTime() - start.getTime()) / 60000)
    }
    return {
      dead,
      timed: minutes.length,
      median: median(minutes),
      mean: minutes.length ? minutes.reduce((a, b) => a + b, 0) / minutes.length : null,
      data: BUCKETS.map((b, i) => ({
        key: b.label,
        count: minutes.filter((m) => m < b.maxMinutes && (i === 0 || m >= BUCKETS[i - 1].maxMinutes)).length,
      })),
    }
  }, [entries, clock])

  const muted = 'text-subtle'
  const text = 'text-foreground'

  return (
    <ProtectionLatencyCard
      data={stats.data}
      label={CLOCK_LABEL[clock]}
      subtitle={
        <>
          median <span className={`font-semibold ${text}`}>{formatDuration(stats.median)}</span>
          {' · '}mean <span className={`font-semibold ${text}`}>{formatDuration(stats.mean)}</span>
          {' · '}{stats.timed} of {stats.dead} dead timed
        </>
      }
      fastKeys={['<1 d']}
      fastLabel="dead within a day"
      colorFor={bucketColor}
      className="col-span-full"
      isDark={isDark}
      muted={muted}
      text={text}
    />
  )
}
